/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import AiSearchIcon from '@atlaskit/icon-lab/core/ai-search';
import TableIcon from '@atlaskit/icon/core/table';
import { Box, Flex, Text } from '@atlaskit/primitives/compiled';
import Tile from '@atlaskit/tile';
import { token } from '@atlaskit/tokens';
import { KnowledgeDiscoveryJiraIntentDetectionModel } from '@atlassian/search-client';
import { addRecentSearch, RECENT_SEARCH_KEY } from '@atlassian/search-common';
import { AllAppsIcon } from '@atlassian/search-common/icons';

import { ReturnIcon } from '../../../../common/constants/icons/return';
import { FlipList } from '../../../../common/ui/flip-list';
import { SearchResult } from '../../../../common/ui/quick-find/search-dialog-result';
import { sha256Hash } from '../../../../common/utils/events/utils';
import { serveJiraNINFpsNLQNavigation } from '../../../../common/utils/experiments/experiment-exports';
import { INTENT_DETECTION, IntentMode } from '../../../../common/utils/intent-detection';
import { onAdvancedSearchLinkSelected } from '../../../../common/utils/quick-find/events/advanced-search-link-selected';
import { onIntentRouterSelected } from '../../../../common/utils/quick-find/events/intent-router-selected';
import {
	useQuickFindAnalytics,
	useQuickFindAttributes,
} from '../../../../controllers/quick-find/analytics';
import { useIntentDetectionVitalStats } from '../../../../controllers/quick-find/use-intent-detection-vitalstats';
import { isOpenInNewTabInteraction } from '../../../../controllers/quick-find/utils';
import {
	usePrimaryProduct,
	useSearchSessionId,
	useUser,
} from '../../../../controllers/store/bootstrap';
import {
	useQuickFindActions,
	useUpdateRecentQueries,
} from '../../../../controllers/store/quick-find';
import { useSearchActions } from '../../../../controllers/store/search';

import { messages } from './messages';
import { useIntentDetectionService } from './use-intent-detection-service';
import { useIntentRouterKeyboardPreselection } from './use-intent-router-keyboard-preselection';
import { useIntentRoutes } from './use-intent-routes';
import { useIntentRouting } from './use-intent-routing';
import { modelIdValidator } from './utils';

const styles = cssMap({
	root: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.025'),
	},
	intentRouterQuery: {
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
});

/**
 * Static IntentRouter rows.
 *
 * Returns two destination rows using SearchResult so they integrate
 * with @atlassian/search-dialog keyboard navigation.
 *
 */
export const IntentRouter = ({
	query,
	minQueryLength,
}: {
	query: string;
	minQueryLength: number;
}): React.JSX.Element => {
	const intl = useIntl();
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useQuickFindAttributes();
	const [searchSessionId] = useSearchSessionId();
	const [user] = useUser();
	const [primaryProduct] = usePrimaryProduct();
	const updateRecentQueries = useUpdateRecentQueries();
	const { setQuickFindOpen } = useQuickFindActions();
	const { clearAllFilters } = useSearchActions();
	const { isTaskActive } = useIntentDetectionVitalStats();

	const intentDetectionModelId = serveJiraNINFpsNLQNavigation({
		primaryProduct,
		fireLayerExposure: false,
	})?.modelId;

	const modelId = modelIdValidator(intentDetectionModelId)
		? intentDetectionModelId
		: KnowledgeDiscoveryJiraIntentDetectionModel.GNIN_HEURISTIC;

	const allAppsLabel = intl.formatMessage(messages.allApps);
	const jiraWorkItemsLabel = intl.formatMessage(messages.jiraWorkItems);

	const { defaultMode, allAppsUrl, jiraWorkItemsUrl } = useIntentRoutes({
		query,
		minQueryLength,
	});

	// Use the initial primary mode as the default mode for intent detection
	const intentDetection = useIntentDetectionService({ defaultMode });

	// Omitting pauseAndResume from the result as it is not needed for intent router.
	// If we need it in the future, we can add it back to the result.
	const { primaryMode, isPaused: disabled } = useIntentRouting({
		query,
		debounceMs: 250,
		intentDetection,
		initialPrimaryMode: defaultMode,
		minQueryLength,
	});

	// Keep keyboard selection in sync with the active intent mode and query.
	useIntentRouterKeyboardPreselection({ primaryMode, query });

	const handleResultClicked = useCallback(
		(
			event: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>,
			selectedMode: IntentMode,
			buttonPosition: 0 | 1,
		) => {
			const newTab = isOpenInNewTabInteraction(event);
			if (!newTab) {
				setQuickFindOpen(false);
				// Keep parity with "Enter on input" behaviour: reset QF filters as FPS is the source of truth.
				clearAllFilters();
			}

			const trimmedQuery = query.trim();
			if (trimmedQuery !== '' && user && user.id && primaryProduct) {
				addRecentSearch(trimmedQuery, user.id, primaryProduct, RECENT_SEARCH_KEY);
				updateRecentQueries?.();
			}

			const queryHash = sha256Hash(query);
			fireAnalyticsEvent(
				onIntentRouterSelected({
					attributes: {
						searchSessionId: searchSessionId || '',
						selectedMode,
						queryClassification: primaryMode,
						queryHash,
						buttonPosition,
						queryLength: query.length,
						networkResponded: !isTaskActive(),
						modelId,
					},
				}),
			);

			fireAnalyticsEvent(
				onAdvancedSearchLinkSelected({
					actionSubjectId: 'jiraAdvancedSearchLink',
					attributes: {
						...commonAttributes,
						newTab,
						isLoading: isTaskActive(),
						selectedMode,
						buttonPosition,
						queryClassification: primaryMode,
						modelId,
					},
					nonPrivacySafeAttributes,
				}),
			);
		},
		[
			clearAllFilters,
			commonAttributes,
			fireAnalyticsEvent,
			modelId,
			nonPrivacySafeAttributes,
			primaryMode,
			primaryProduct,
			query,
			searchSessionId,
			setQuickFindOpen,
			updateRecentQueries,
			user,
			isTaskActive,
		],
	);

	// Update the global intent detection result
	INTENT_DETECTION.update({
		mode: primaryMode,
		route: primaryMode === IntentMode.FPS ? allAppsUrl : jiraWorkItemsUrl,
		query, // store the query to aid hydration
		modelId,
	});

	const orderedModes: IntentMode[] =
		primaryMode === IntentMode.GNIN
			? [IntentMode.GNIN, IntentMode.FPS]
			: [IntentMode.FPS, IntentMode.GNIN];

	const rowsByMode: Record<IntentMode, React.JSX.Element> = {
		[IntentMode.FPS]: (
			<SearchResult
				key="intent-all-apps"
				id="intent-all-apps"
				resultType="intentRouter"
				icon={
					<Tile label="" size="small">
						<AiSearchIcon label={allAppsLabel} />
					</Tile>
				}
				title={
					<Flex direction="row" alignItems="center" gap="space.100">
						<Box xcss={styles.intentRouterQuery}>
							{query || intl.formatMessage(messages.searchForAllApps)}
						</Box>
						<Text size="small" color="color.text.subtlest">
							––
						</Text>
						<Text size="small" color="color.text.subtlest">
							{intl.formatMessage(messages.allApps)}
						</Text>
						<AllAppsIcon />
					</Flex>
				}
				url={allAppsUrl}
				onResultClicked={(event) =>
					handleResultClicked(event, IntentMode.FPS, primaryMode === IntentMode.FPS ? 0 : 1)
				}
				highlightedRightMetadata={<ReturnIcon />}
			/>
		),
		[IntentMode.GNIN]: (
			<SearchResult
				key="intent-jira-work-items"
				id="intent-jira-work-items"
				resultType="intentRouter"
				icon={
					<Tile label="" size="small">
						<TableIcon label={jiraWorkItemsLabel} />
					</Tile>
				}
				title={
					<Flex direction="row" gap="space.100">
						<Box xcss={styles.intentRouterQuery}>
							{query || intl.formatMessage(messages.searchForJiraWorkItems)}
						</Box>
						<Text size="small" color="color.text.subtlest">
							––
						</Text>
						<Text size="small" color="color.text.subtlest">
							{intl.formatMessage(messages.jiraWorkItems)}
						</Text>
					</Flex>
				}
				url={jiraWorkItemsUrl}
				onResultClicked={(event) =>
					handleResultClicked(event, IntentMode.GNIN, primaryMode === IntentMode.GNIN ? 0 : 1)
				}
				highlightedRightMetadata={<ReturnIcon />}
			/>
		),
	};

	const order = orderedModes.map((mode) =>
		mode === IntentMode.FPS ? 'intent-all-apps' : 'intent-jira-work-items',
	);

	return (
		<FlipList
			order={order}
			disabled={disabled}
			containerAs="div"
			containerProps={{ role: 'list' }}
			itemAs="div"
			renderItem={(key) => {
				return key === 'intent-all-apps' ? rowsByMode[IntentMode.FPS] : rowsByMode[IntentMode.GNIN];
			}}
			xcss={styles.root}
		/>
	);
};
